import { useState, FocusEvent, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { Input, LabeledContainer, Select } from '@schibsted-svp/react-ui';
import cln from 'classnames';
import { z } from 'zod';
import { MediaLiveInputType } from 'services/admin-bff/contants';
import { AdvancedToggleButton } from '../AdvancedToggleButton';
import { findSingleOptionWithDefaultValue } from '../../../../ui/Form/Select';
import QuestionMark from '../../../../ui/QuestionMark';
import { useMediaLiveInputFormContext } from './hooks';
import css from './MediaLiveSrtSettings.module.scss';

const algorithmOptions = [
    { value: 'AES128', label: 'AES128' },
    { value: 'AES192', label: 'AES192' },
    { value: 'AES256', label: 'AES256' },
];

const pbkeylenToAlgorithm = (pbkeylen: string): string => {
    switch (pbkeylen) {
        case '32':
            return 'AES256';
        case '24':
            return 'AES192';
        case '16':
            return 'AES128';
        default:
            return pbkeylen;
    }
};

const validateParsedSchema = (result: z.SafeParseReturnType<unknown, unknown>): string | undefined => {
    if (result.success === false) {
        return result.error.errors.map((error) => error.message).join(', ');
    }
    return undefined;
};

const latencySchema = z.coerce.number().min(120).max(15000);

const latencyValidator = (value: unknown) => {
    if (value === '') {
        return undefined;
    }
    const result = latencySchema.safeParse(value);
    return validateParsedSchema(result);
};

const passphraseSchema = z.string().min(10).max(80);

const passphrasePushValidator = (value: unknown) => {
    if (value === '') {
        return 'Passphrase is required for PUSH type';
    }
    const result = passphraseSchema.safeParse(value);
    return validateParsedSchema(result);
};

const handleFocus = (event: FocusEvent<HTMLInputElement>) => event.target.select();

type MediaLiveSrtSettingsProps = {
    srtSettings?: {
        streamId?: string;
        address?: string;
        port?: string;
        latency?: number;
        algorithm?: string;
        passphrase?: string;
    };
    inputType?: keyof typeof MediaLiveInputType;
    pullSource?: string;
};

export function MediaLiveSrtSettings({ srtSettings, inputType, pullSource }: MediaLiveSrtSettingsProps) {
    const [isExpanded, setIsExpanded] = useState(false);

    const { formApi } = useMediaLiveInputFormContext();
    const {
        register,
        control,
        formState: { errors, submitCount },
    } = formApi;

    useEffect(() => {
        if (submitCount > 0 && formApi.getFieldState('srtPassphrase').error) {
            setIsExpanded(true);
        }
    }, [submitCount, formApi]);

    const pullSourceParams = URL.parse(pullSource)?.searchParams;

    const passphrase = srtSettings?.passphrase || pullSourceParams?.get('passphrase');
    const algorithm = srtSettings?.algorithm || pbkeylenToAlgorithm(pullSourceParams?.get('pbkeylen'));
    const latency = srtSettings?.latency || pullSourceParams?.get('latency');
    const streamId = srtSettings?.streamId || pullSourceParams?.get('streamid');

    return (
        <div className={css.container}>
            <div className={cln(css.settings, { [css.hidden]: !isExpanded })}>
                <LabeledContainer
                    label={
                        <>
                            Passphrase
                            <QuestionMark content="When the source is encrypted, the passphrase is used to decrypt the content." />
                        </>
                    }
                >
                    {srtSettings || passphrase ? (
                        <Input value={passphrase} key={passphrase} onFocus={handleFocus} readOnly />
                    ) : (
                        <Input
                            {...register('srtPassphrase', {
                                shouldUnregister: true,
                                validate: inputType === MediaLiveInputType.PUSH ? passphrasePushValidator : undefined,
                            })}
                            error={errors?.srtPassphrase?.message}
                        />
                    )}
                </LabeledContainer>
                <LabeledContainer
                    label={
                        <>
                            Algorithm
                            <QuestionMark content="The algorithm used for encryption." />
                        </>
                    }
                >
                    {srtSettings || algorithm ? (
                        <Input value={algorithm} key={algorithm} onFocus={handleFocus} readOnly />
                    ) : (
                        <Controller
                            name="srtAlgorithm"
                            control={control}
                            shouldUnregister
                            render={({ field }) => (
                                <Select
                                    value={findSingleOptionWithDefaultValue(algorithmOptions, 'AES128')(field.value)}
                                    options={algorithmOptions}
                                    onChange={(value) => field.onChange(value.value)}
                                />
                            )}
                        />
                    )}
                </LabeledContainer>
                <LabeledContainer
                    label={
                        <>
                            Minimum latency
                            <QuestionMark content="The minimum size of the buffer (in ms). Valid range: 120–15,000. Defaults to 1,000" />
                        </>
                    }
                >
                    {srtSettings || latency ? (
                        <Input value={latency} key={latency} onFocus={handleFocus} readOnly />
                    ) : (
                        <Input
                            {...register('srtLatency', { shouldUnregister: true, validate: latencyValidator })}
                            placeholder="1000"
                            error={errors?.srtLatency?.message}
                        />
                    )}
                </LabeledContainer>
                <LabeledContainer
                    label={
                        <>
                            Stream ID
                            <QuestionMark content="An optional identifier for the stream. This identifier can be used to communicate information about the stream from the caller to the listener." />
                        </>
                    }
                >
                    {srtSettings || streamId ? (
                        <Input value={streamId} key={streamId} onFocus={handleFocus} readOnly />
                    ) : (
                        <Input {...register('srtStreamId', { shouldUnregister: true })} />
                    )}
                </LabeledContainer>
            </div>
            <AdvancedToggleButton isExpanded={isExpanded} onClick={() => setIsExpanded(!isExpanded)} />
        </div>
    );
}
